import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import {
  TrendingUp,
  Sparkles,
  Star,
  ArrowDown,
  BadgeCheck,
  Radar,
  Eye,
  Phone,
  Flame,
  ShieldAlert,
  CalendarClock,
  FileWarning,
  Trophy,
} from "lucide-react";

/* Chart geometry */
const W = 720;
const H = 320;
const PADDING_L = 44;
const PADDING_R = 28;
const PADDING_T = 24;
const PADDING_B = 40;
const MAX_DAY = 30;

/* Data points: [day, rating] */
const POINTS = [
  [0, 3.2],
  [3, 3.15],
  [5, 3.1],
  [8, 3.2],
  [11, 3.55],
  [13, 3.7],
  [17, 4.0],
  [20, 4.3],
  [24, 4.55],
  [27, 4.7],
  [30, 4.8],
];

/* Annotated events */
const EVENTS = [
  { day: 4, label: "First 2 removals filed", sub: "Fake reviews · Never visited", icon: FileWarning, color: "#94A3B8" },
  { day: 10, label: "3 reviews removed", sub: "Confirmed by Google", icon: BadgeCheck, color: "#10B981" },
  { day: 18, label: "Escalation resolved", sub: "Remaining 3 removed", icon: ShieldAlert, color: "#D4AF37" },
  { day: 26, label: "Ranking lift confirmed", sub: "+3 positions in local pack", icon: TrendingUp, color: "#10B981" },
];

const xScale = (x) => PADDING_L + (x / MAX_DAY) * (W - PADDING_L - PADDING_R);
const yScale = (y) => H - PADDING_B - ((y - 3) / (5 - 3)) * (H - PADDING_T - PADDING_B);

/* Cubic-bezier smooth path */
function smoothPath(points) {
  if (points.length < 2) return "";
  const pts = points.map(([d, v]) => [xScale(d), yScale(v)]);
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[i];
    const [x1, y1] = pts[i + 1];
    const mx = (x0 + x1) / 2;
    d += ` C ${mx} ${y0}, ${mx} ${y1}, ${x1} ${y1}`;
  }
  return d;
}

function useAnimatedNumber(target, inView, duration = 1800, decimals = 0) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);
  return decimals ? val.toFixed(decimals) : Math.round(val);
}

/* Interpolate rating at a given day using the POINTS series */
function ratingAtDay(day) {
  for (let i = 0; i < POINTS.length - 1; i++) {
    const [d0, v0] = POINTS[i];
    const [d1, v1] = POINTS[i + 1];
    if (day >= d0 && day <= d1) {
      const t = (day - d0) / (d1 - d0);
      return v0 + (v1 - v0) * t;
    }
  }
  return POINTS[POINTS.length - 1][1];
}

/* --- BEFORE / AFTER mini Google Business Profile cards --- */
function BeforeAfterCard() {
  return (
    <div className="relative flex flex-col gap-4">
      <MiniBusinessCard variant="before" />
      <div className="flex items-center justify-center gap-3 py-1">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/15 to-white/30" />
        <motion.div
          animate={{ y: [0, 4, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="inline-flex items-center gap-2 rounded-full border border-[#10B981]/30 bg-[#10B981]/[0.08] px-3 py-1.5"
        >
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#10B981]">
            30 days later
          </span>
          <ArrowDown className="w-3.5 h-3.5 text-[#10B981]" />
        </motion.div>
        <div className="h-px flex-1 bg-gradient-to-l from-transparent via-white/15 to-white/30" />
      </div>
      <MiniBusinessCard variant="after" />
    </div>
  );
}

function MiniBusinessCard({ variant }) {
  const isBefore = variant === "before";
  const rating = isBefore ? 3.2 : 4.8;
  const reviews = isBefore ? 42 : 63;
  const filled = isBefore ? 3 : 5;

  return (
    <motion.div
      initial={{ opacity: 0, x: isBefore ? -12 : 12 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6, delay: isBefore ? 0.1 : 0.3 }}
      className={`relative rounded-2xl border p-5 overflow-hidden ${
        isBefore
          ? "border-red-500/25 bg-gradient-to-br from-red-500/[0.06] to-transparent"
          : "border-[#10B981]/30 bg-gradient-to-br from-[#10B981]/[0.08] to-transparent"
      }`}
    >
      {/* corner label */}
      <div
        className={`absolute top-0 left-0 text-[10px] uppercase font-bold tracking-widest px-2.5 py-1 rounded-br-lg ${
          isBefore
            ? "bg-red-500/15 text-red-300 border-r border-b border-red-500/25"
            : "bg-[#10B981]/20 text-[#10B981] border-r border-b border-[#10B981]/30"
        }`}
      >
        {isBefore ? "Before" : "After"}
      </div>

      <div className="flex items-start gap-3 mt-4">
        <div
          className={`w-11 h-11 shrink-0 rounded-full grid place-items-center font-display font-bold ${
            isBefore
              ? "bg-red-500/15 text-red-300"
              : "bg-gradient-to-br from-[#10B981] to-[#0e8f66] text-white"
          }`}
        >
          RB
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-sm truncate">Riverside Bistro</div>
          <div className="text-white/45 text-xs">Italian restaurant · Google</div>

          <div className="mt-2 flex items-center gap-2">
            <span
              className={`font-display font-bold text-lg tabular-nums ${
                isBefore ? "text-red-300" : "text-[#10B981]"
              }`}
            >
              {rating.toFixed(1)}
            </span>
            <div className="flex gap-0.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star
                  key={i}
                  className={`w-3 h-3 ${
                    i < filled
                      ? isBefore
                        ? "fill-red-400 text-red-400"
                        : "fill-[#D4AF37] text-[#D4AF37]"
                      : "fill-white/10 text-white/15"
                  }`}
                />
              ))}
            </div>
            <span className="text-white/40 text-xs">({reviews})</span>
          </div>
        </div>
      </div>

      {/* Mock metrics */}
      <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
        <MiniStat
          icon={<Eye className="w-3 h-3" />}
          label="Weekly views"
          value={isBefore ? "1,240" : "1,760"}
          delta={isBefore ? "—" : "+42%"}
          positive={!isBefore}
        />
        <MiniStat
          icon={<Phone className="w-3 h-3" />}
          label="Direction / calls"
          value={isBefore ? "38" : "71"}
          delta={isBefore ? "—" : "+87%"}
          positive={!isBefore}
        />
      </div>

      {/* Preview review row */}
      <div className="mt-4 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-[11px] text-white/60 leading-relaxed">
        <div className="flex items-center gap-1.5 mb-1">
          {isBefore ? (
            <>
              <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
              <span className="text-red-300 font-semibold">1★ Never visited — fake</span>
            </>
          ) : (
            <>
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
              <span className="text-[#10B981] font-semibold">5★ Verified customer</span>
            </>
          )}
        </div>
        <div className="text-white/50 italic truncate">
          {isBefore
            ? '"Owner is rude, wouldn\'t recommend..."'
            : '"Best pasta in town. Warm service."'}
        </div>
      </div>
    </motion.div>
  );
}

function MiniStat({ icon, label, value, delta, positive }) {
  return (
    <div className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2">
      <div className="flex items-center gap-1 text-white/40">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="font-display font-bold text-white text-sm tabular-nums">{value}</span>
        <span
          className={`text-[10px] font-semibold ${
            positive ? "text-[#10B981]" : "text-white/30"
          }`}
        >
          {delta}
        </span>
      </div>
    </div>
  );
}

/* --- Main animated chart --- */
function AnimatedChart({ inView }) {
  const pathRef = useRef(null);
  const [pathLen, setPathLen] = useState(0);
  const [progress, setProgress] = useState(0); // 0..1

  const linePath = smoothPath(POINTS);
  const areaPath =
    linePath +
    ` L ${xScale(POINTS[POINTS.length - 1][0])} ${H - PADDING_B}` +
    ` L ${xScale(POINTS[0][0])} ${H - PADDING_B} Z`;

  useEffect(() => {
    if (pathRef.current) {
      setPathLen(pathRef.current.getTotalLength());
    }
  }, [pathRef.current]);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 2400;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setProgress(eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView]);

  const currentDay = Math.round(progress * MAX_DAY);
  const currentRating = ratingAtDay(currentDay);

  /* pulse dot position — sample along path length */
  const [pulsePt, setPulsePt] = useState({ x: xScale(0), y: yScale(POINTS[0][1]) });
  useEffect(() => {
    if (!pathRef.current || !pathLen) return;
    const pt = pathRef.current.getPointAtLength(pathLen * progress);
    setPulsePt({ x: pt.x, y: pt.y });
  }, [progress, pathLen]);

  return (
    <div className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.04] via-white/[0.02] to-transparent p-5 sm:p-7 overflow-hidden">
      {/* subtle grid + glow */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse at 50% 50%, black 40%, transparent 85%)",
        }}
      />
      <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#10B981]/12 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-10 w-56 h-56 rounded-full bg-[#D4AF37]/10 blur-3xl" />

      {/* Header */}
      <div className="relative flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">
            Star rating · Live replay
          </div>
          <div className="mt-1 flex items-end gap-3">
            <div className="font-display text-4xl sm:text-5xl font-bold tabular-nums text-white leading-none">
              {currentRating.toFixed(1)}
            </div>
            <div className="flex gap-0.5 mb-1.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 transition-colors duration-300 ${
                    currentRating >= i + 0.6
                      ? "fill-[#D4AF37] text-[#D4AF37]"
                      : "fill-white/10 text-white/15"
                  }`}
                />
              ))}
            </div>
          </div>
          <div className="mt-1 text-[11px] text-white/45">
            Day <span className="tabular-nums text-white/80">{currentDay}</span> of {MAX_DAY}
          </div>
        </div>

        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">
            Delta
          </div>
          <div className="mt-1 inline-flex items-center gap-1 text-[#10B981] font-display text-3xl font-bold tabular-nums">
            <TrendingUp className="w-5 h-5" />
            +{(currentRating - 3.2).toFixed(1)}
          </div>
          <div className="mt-1 flex items-center justify-end gap-1.5 text-[11px] text-[#10B981]/80">
            <Flame className="w-3 h-3" />
            outperforming 92% of local peers
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[240px] sm:h-[300px]">
          <defs>
            <linearGradient id="areaFill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#10B981" stopOpacity="0.4" />
              <stop offset="60%" stopColor="#10B981" stopOpacity="0.08" />
              <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="lineGrad" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="#94A3B8" />
              <stop offset="40%" stopColor="#D4AF37" />
              <stop offset="100%" stopColor="#10B981" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Reference lines */}
          {[3, 3.5, 4, 4.5, 5].map((v) => (
            <g key={v}>
              <line
                x1={PADDING_L}
                x2={W - PADDING_R}
                y1={yScale(v)}
                y2={yScale(v)}
                stroke="rgba(255,255,255,0.05)"
                strokeDasharray="3 6"
              />
              <text
                x={PADDING_L - 8}
                y={yScale(v) + 4}
                textAnchor="end"
                fill="rgba(255,255,255,0.32)"
                fontSize="10"
                fontFamily="Satoshi, ui-sans-serif"
              >
                {v.toFixed(1)}★
              </text>
            </g>
          ))}

          {/* Event vertical guides */}
          {EVENTS.map((e, i) => (
            <motion.g
              key={i}
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.5 + (e.day / MAX_DAY) * 2, duration: 0.4 }}
            >
              <line
                x1={xScale(e.day)}
                x2={xScale(e.day)}
                y1={yScale(ratingAtDay(e.day)) - 8}
                y2={PADDING_T + 4}
                stroke={e.color}
                strokeDasharray="2 4"
                strokeWidth="1"
                opacity="0.5"
              />
              <circle
                cx={xScale(e.day)}
                cy={PADDING_T + 4}
                r="3"
                fill={e.color}
              />
            </motion.g>
          ))}

          {/* Area (fades in) */}
          <motion.path
            d={areaPath}
            fill="url(#areaFill)"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 1.6, delay: 0.6 }}
          />

          {/* Animated line */}
          <motion.path
            ref={pathRef}
            d={linePath}
            fill="none"
            stroke="url(#lineGrad)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={pathLen}
            strokeDashoffset={pathLen ? pathLen * (1 - progress) : pathLen}
            style={{ filter: "url(#glow)" }}
          />

          {/* Data points */}
          {POINTS.map((p, i) => {
            const showAt = p[0] / MAX_DAY;
            const visible = progress >= showAt - 0.02;
            return (
              <motion.circle
                key={i}
                cx={xScale(p[0])}
                cy={yScale(p[1])}
                r="3"
                fill={p[1] < 3.5 ? "#94A3B8" : p[1] < 4.2 ? "#D4AF37" : "#10B981"}
                animate={visible ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              />
            );
          })}

          {/* Pulse dot (traveling head) */}
          {progress > 0.02 && progress < 0.99 && (
            <>
              <circle cx={pulsePt.x} cy={pulsePt.y} r="9" fill="#10B981" opacity="0.25">
                <animate attributeName="r" values="6;14;6" dur="1.4s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.35;0.05;0.35" dur="1.4s" repeatCount="indefinite" />
              </circle>
              <circle cx={pulsePt.x} cy={pulsePt.y} r="4" fill="#10B981" />
            </>
          )}

          {/* Terminal star badge when finished */}
          {progress >= 0.99 && (
            <motion.g
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 220, damping: 16 }}
            >
              <circle
                cx={xScale(MAX_DAY)}
                cy={yScale(4.8)}
                r="12"
                fill="#0A0F1C"
                stroke="#D4AF37"
                strokeWidth="2"
              />
              <g transform={`translate(${xScale(MAX_DAY) - 6}, ${yScale(4.8) - 6})`}>
                <path
                  d="M6 0 L7.4 4.3 L12 4.3 L8.3 7 L9.7 11.3 L6 8.6 L2.3 11.3 L3.7 7 L0 4.3 L4.6 4.3 Z"
                  fill="#D4AF37"
                />
              </g>
            </motion.g>
          )}

          {/* x-axis labels */}
          {[0, 10, 20, 30].map((d) => (
            <text
              key={d}
              x={xScale(d)}
              y={H - 14}
              textAnchor="middle"
              fill="rgba(255,255,255,0.35)"
              fontSize="10"
              fontFamily="Satoshi"
            >
              Day {d}
            </text>
          ))}
        </svg>

        {/* Legend */}
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-white/45">
          <div className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#94A3B8]" />
            Pre-audit baseline
          </div>
          <div className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />
            Removal phase
          </div>
          <div className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#10B981]" />
            Post-recovery
          </div>
        </div>
      </div>
    </div>
  );
}

/* --- Timeline event chips --- */
function EventTimeline({ inView }) {
  const items = [
    { day: 0, icon: CalendarClock, label: "Free audit completed", color: "text-white/60", bg: "bg-white/[0.04]", border: "border-white/10" },
    { day: 4, icon: FileWarning, label: "First 2 removals filed", color: "text-white/70", bg: "bg-white/[0.04]", border: "border-white/10" },
    { day: 10, icon: BadgeCheck, label: "3 reviews removed", color: "text-[#10B981]", bg: "bg-[#10B981]/[0.08]", border: "border-[#10B981]/30" },
    { day: 18, icon: ShieldAlert, label: "Escalation cleared", color: "text-[#D4AF37]", bg: "bg-[#D4AF37]/[0.08]", border: "border-[#D4AF37]/30" },
    { day: 26, icon: TrendingUp, label: "Ranking lift confirmed", color: "text-[#10B981]", bg: "bg-[#10B981]/[0.08]", border: "border-[#10B981]/30" },
    { day: 30, icon: Trophy, label: "4.8★ maintained", color: "text-[#D4AF37]", bg: "bg-[#D4AF37]/[0.10]", border: "border-[#D4AF37]/35" },
  ];
  return (
    <div className="mt-8 flex flex-wrap gap-2">
      {items.map((it, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 8 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.4, delay: 0.9 + i * 0.09 }}
          className={`group inline-flex items-center gap-2 rounded-full border ${it.border} ${it.bg} px-3 py-1.5 text-xs`}
        >
          <span className="text-[10px] uppercase tracking-widest text-white/40 font-bold tabular-nums">
            Day {String(it.day).padStart(2, "0")}
          </span>
          <span className={`inline-flex items-center gap-1.5 font-semibold ${it.color}`}>
            <it.icon className="w-3.5 h-3.5" />
            {it.label}
          </span>
        </motion.div>
      ))}
    </div>
  );
}

/* --- Bottom stat grid --- */
function StatGrid({ inView }) {
  const removals = useAnimatedNumber(6, inView, 1600);
  const days = useAnimatedNumber(17, inView, 1800);
  const impressions = useAnimatedNumber(42, inView, 2000);
  const rating = useAnimatedNumber(1.6, inView, 2200, 1);

  const stats = [
    { icon: FileWarning, label: "Removals filed", value: removals, unit: "", color: "emerald" },
    { icon: CalendarClock, label: "Avg. days per removal", value: days, unit: "", color: "gold" },
    { icon: Eye, label: "Weekly impressions", value: impressions, unit: "%", color: "emerald", prefix: "+" },
    { icon: TrendingUp, label: "Star rating delta", value: rating, unit: "★", color: "gold", prefix: "+" },
  ];

  return (
    <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3">
      {stats.map((s, i) => {
        const isEmerald = s.color === "emerald";
        return (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 12 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
            className={`relative rounded-2xl border p-5 overflow-hidden ${
              isEmerald
                ? "border-[#10B981]/25 bg-[#10B981]/[0.04]"
                : "border-[#D4AF37]/25 bg-[#D4AF37]/[0.04]"
            }`}
          >
            <div
              className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-bold ${
                isEmerald ? "text-[#10B981]" : "text-[#D4AF37]"
              }`}
            >
              <s.icon className="w-3 h-3" />
              {s.label}
            </div>
            <div className="mt-3 font-display text-3xl sm:text-4xl font-bold text-white tabular-nums leading-none">
              {s.prefix || ""}
              {s.value}
              <span className="text-white/40 text-xl">{s.unit}</span>
            </div>
            {/* subtle sparkline decoration */}
            <svg
              className="absolute bottom-0 right-0 opacity-30 pointer-events-none"
              viewBox="0 0 80 30"
              width="80"
              height="30"
            >
              <path
                d="M0 22 L15 20 L28 24 L40 14 L52 10 L65 6 L80 2"
                stroke={isEmerald ? "#10B981" : "#D4AF37"}
                strokeWidth="1.5"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          </motion.div>
        );
      })}
    </div>
  );
}

export default function RatingChart() {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.2 });

  return (
    <section ref={sectionRef} className="relative py-24 lg:py-32 overflow-hidden">
      {/* section glow */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(700px 400px at 80% 20%, rgba(16,185,129,0.06), transparent 60%), radial-gradient(500px 300px at 10% 90%, rgba(212,175,55,0.05), transparent 60%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur px-3 py-1.5 text-[11px] text-white/70"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#10B981]" />
              </span>
              <span className="uppercase tracking-[0.2em] font-semibold">
                Recovery, visualized
              </span>
              <span className="text-white/30">·</span>
              <span className="text-white/60">Case study · Riverside Bistro</span>
            </motion.div>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-5 leading-[1.05]">
              From{" "}
              <span className="relative inline-block">
                <span className="text-white/40">3.2</span>
                <motion.span
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  style={{ transformOrigin: "left" }}
                  className="absolute left-0 right-0 top-1/2 h-[3px] bg-red-400/70 rounded-full"
                />
              </span>{" "}
              to{" "}
              <span className="relative inline-block text-[#10B981]">
                4.8
                <motion.span
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.7 }}
                  style={{ transformOrigin: "left" }}
                  className="absolute -bottom-1 left-0 right-0 h-1 bg-[#10B981]/40 rounded-full"
                />
              </span>{" "}
              in <span className="text-[#D4AF37]">30 days</span>.
            </h2>
            <p className="mt-5 text-white/60 text-base leading-relaxed max-w-xl">
              A real-world case study replayed frame-by-frame. Six policy-violating reviews removed,
              a ranking lift confirmed at day 26, and a 4.8★ average that's held ever since — driven
              entirely by removals and legitimate review-generation workflows. No fake stars. No
              black-hat tactics.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 pr-6 flex items-center gap-3"
          >
            <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#10B981]/10 border border-[#10B981]/30">
              <Radar className="w-5 h-5 text-[#10B981]" />
            </div>
            <div className="text-sm">
              <div className="font-display font-bold text-white">Verified outcome</div>
              <div className="text-white/50 text-xs">Confirmed in Google Business dashboard</div>
            </div>
          </motion.div>
        </div>

        {/* Main split */}
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
          <div className="lg:col-span-5">
            <BeforeAfterCard />
          </div>
          <div className="lg:col-span-7">
            <AnimatedChart inView={inView} />
            <EventTimeline inView={inView} />
          </div>
        </div>

        {/* Stat grid */}
        <StatGrid inView={inView} />

        {/* Bottom quote strip */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 rounded-2xl border border-white/10 bg-gradient-to-r from-[#10B981]/[0.05] via-white/[0.02] to-[#D4AF37]/[0.05] p-6 lg:p-8 flex flex-wrap items-center justify-between gap-6"
        >
          <div className="flex items-center gap-4 max-w-2xl">
            <div className="w-11 h-11 shrink-0 rounded-full bg-gradient-to-br from-[#10B981] to-[#0e8f66] grid place-items-center font-display font-bold text-white">
              MA
            </div>
            <div>
              <div className="text-white/80 text-sm leading-relaxed italic">
                "Reservations doubled the following month. The pay-after model is the only reason I
                trusted them to begin with."
              </div>
              <div className="mt-2 text-xs text-white/45">
                <span className="text-white font-semibold">Marco Alvarez</span> · Owner, Riverside Bistro
              </div>
            </div>
          </div>
          <a
            href="#audit"
            className="group inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#0ea774] text-white text-sm font-semibold px-5 h-11 transition-colors shadow-lg shadow-emerald-500/25"
          >
            <Sparkles className="w-4 h-4" />
            Get results like this
          </a>
        </motion.div>
      </div>
    </section>
  );
}
